import { useState } from 'preact/hooks';
import type { LiveDigest } from '../api/types';
import styles from './LiveDigestBar.module.css';

interface LiveDigestBarProps {
  digest: LiveDigest;
}

// TODO: Extract to i18n when PWA localization is added
const SENTIMENT_LABELS: Record<string, string> = {
  positive: 'Positive',
  neutral: 'Neutral',
  mixed: 'Mixed',
  negative: 'Negative',
};

const SENTIMENT_COLORS: Record<string, string> = {
  positive: '#059669',
  neutral: '#6B7280',
  mixed: '#D97706',
  negative: '#DC2626',
};

export function LiveDigestBar({ digest }: LiveDigestBarProps) {
  const [expanded, setExpanded] = useState(false);

  if (digest.contributionCount === 0) return null;

  const progressPercent = digest.arcProgress.totalPhases > 0
    ? ((digest.arcProgress.phaseIndex + 1) / digest.arcProgress.totalPhases) * 100
    : 0;

  return (
    <div class={styles.container}>
      <button
        class={styles.toggle}
        onClick={() => setExpanded(prev => !prev)}
        type="button"
      >
        <span class={styles.toggleLabel}>
          What's been shared
          <span class={styles.count}>
            {digest.contributionCount} {digest.contributionCount === 1 ? 'take' : 'takes'} from {digest.contributorCount} {digest.contributorCount === 1 ? 'person' : 'people'}
          </span>
        </span>
        <span class={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}>&#9660;</span>
      </button>

      {expanded && (
        <div class={styles.content}>
          {/* Arc progress */}
          <div class={styles.arcProgress}>
            <div class={styles.arcTrack}>
              <div class={styles.arcFill} style={{ width: `${progressPercent}%` }} />
            </div>
            <div class={styles.arcMeta}>
              <span>Phase {digest.arcProgress.phaseIndex + 1} of {digest.arcProgress.totalPhases}</span>
              {digest.arcProgress.targetEmotion && (
                <span class={styles.targetEmotion}>{digest.arcProgress.targetEmotion}</span>
              )}
            </div>
          </div>

          {/* Recent voices */}
          {digest.recentVoices.length > 0 && (
            <div class={styles.voices}>
              {digest.recentVoices.slice(0, 3).map((voice) => (
                <div key={`${voice.displayName}-${voice.contributionType}`} class={styles.voiceRow}>
                  <span class={styles.voiceAvatar}>
                    {voice.displayName.charAt(0).toUpperCase()}
                  </span>
                  <div class={styles.voiceInfo}>
                    <span class={styles.voiceName}>{voice.displayName}</span>
                    <span class={styles.voiceSnippet}>{voice.snippet}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Themes + sentiment */}
          <div class={styles.footer}>
            {digest.emergingThemes.length > 0 && (
              <div class={styles.themes}>
                {digest.emergingThemes.map(theme => (
                  <span key={theme} class={styles.themeTag}>{theme}</span>
                ))}
              </div>
            )}
            <span
              class={styles.sentimentDot}
              style={{ backgroundColor: SENTIMENT_COLORS[digest.sentiment] ?? '#6B7280' }}
              title={SENTIMENT_LABELS[digest.sentiment] ?? 'Neutral'}
            />
          </div>
        </div>
      )}
    </div>
  );
}
